import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  SimpleGrid,
  Skeleton,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { format, parseISO } from 'date-fns';
import {
  Copy,
  Edit,
  Info,
  Mail,
  Phone,
  Search,
  UserCheck,
  UserCog,
  Users,
  UserX,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import DefaultPage from '../../components/shared/DefaultPage';
import { Input } from '../../components/shared/Input';
import InputSelector from '../../components/shared/Inputs/InputSelector';
import Table from '../../components/shared/Table';
import { useAuth } from '../../hooks/auth';
import { usePermissoes } from '../../services/hooks/usePermissoes';
import { useUsuarios } from '../../services/hooks/useUsuarios';
import { useUsuariosPorStatus } from '../../services/hooks/useUsuariosPorStatus';
import { Constants } from '../../shared/constants';
import { phoneMask } from '../../utils/phoneMask';
import AceitaUsuarioModal from './AceitaUsuarioModal';

interface ICard {
  titulo: string;
  icon: any;
  valor: string | number;
  descricao: string;
  isLoading: boolean;
}

const Card: React.FC<ICard> = ({
  titulo,
  icon,
  valor,
  descricao,
  isLoading,
}) => {
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const bg = useColorModeValue('white', 'gray.800');

  return (
    <Flex
      borderWidth="1px"
      bg={bg}
      rounded="2xl"
      flexDir="column"
      px="8"
      py="4"
    >
      <Flex justify="space-between">
        <Text>{titulo}</Text>
        {icon}
      </Flex>
      {isLoading && (
        <Skeleton
          noOfLines={1}
          fontSize="2xl"
          fontWeight="bold"
          w="50px"
          h="40px"
        />
      )}
      {!isLoading && (
        <Text color={color} fontSize="2xl" fontWeight="bold">
          {valor}
        </Text>
      )}
      <Text fontWeight="medium">{descricao}</Text>
    </Flex>
  );
};

const Usuarios = () => {
  // estilos
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const bg = useColorModeValue('white', 'gray.800');

  // estados
  const [showAceite, setShowAceite] = useState(false);
  const [usuarioAceite, setUsuarioAceite] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('0');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const SelecionaUsuarioAceite = (id: number) => {
    setUsuarioAceite(id);
    setShowAceite(true);
  };

  // dados
  const { data: cardsData, isLoading } = useUsuariosPorStatus();
  const { data, isFetching } = useUsuarios(
    debouncedSearch,
    filtroStatus,
    page,
    pageSize,
  );
  const usuariosData = data?.rows ?? [];
  const { user } = useAuth();
  const { permite } = usePermissoes();
  const { PERMISSAO_CONVIDAR_PARCEIROS } = Constants;

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, filtroStatus, pageSize]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchValue]);

  // constantes
  const toast = useToast();
  const history = useHistory();
  const usuariosStatus = {
    '1': { titulo: 'ativo', color: 'green' },
    '2': { titulo: 'inativo', color: 'red' },
    '3': { titulo: 'aguardando confirmação', color: 'yellow' },
  };

  const colunas = useMemo(
    () => [
      {
        Header: 'Usuário',
        accessor: 'nome',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('#3f3f46', 'white');
          return (
            <Flex>
              <Avatar size="sm" name={row.original.nome} mr="1" />
              <Flex flexDir="column">
                <Text fontWeight="medium" color={color}>
                  {row.original.nome}
                </Text>
                <Text color={color} fontWeight="small" fontSize="small" mt="1">
                  Desde{' '}
                  {format(
                    parseISO(row.original.created_at),
                    'dd/MM/yyyy HH:mm',
                  )}
                </Text>
              </Flex>
            </Flex>
          );
        },
      },
      {
        Header: 'contato',
        accessor: 'contato',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('#3f3f46', 'white');
          return (
            <Flex flexDir="column">
              <Flex>
                <Icon as={Mail} />
                <Text fontWeight="medium" color={color} ml="1">
                  {row.original.email}
                </Text>
              </Flex>
              <Flex mt="1">
                <Icon as={Phone} />
                <Text fontWeight="medium" color={color} ml="1">
                  {phoneMask(row.original.telefone)}
                </Text>
              </Flex>
            </Flex>
          );
        },
      },
      {
        Header: 'status',
        accessor: 'status',
        Cell: ({ row }: { row: any }) => {
          const rowStatus = row.original.status;
          return (
            <>
              {rowStatus === '3' ? (
                <Tooltip label="clique para ativar">
                  <Badge
                    p="2"
                    rounded="xl"
                    whiteSpace="normal"
                    wordBreak="break-word"
                    maxW="120px"
                    w="100%"
                    textAlign="center"
                    cursor="pointer"
                    border="1px"
                    _hover={{
                      bg: 'transparent',
                      border: '-1px solid',
                      borderColor: `${usuariosStatus?.[rowStatus]?.color}.500`,
                    }}
                    colorScheme={usuariosStatus?.[rowStatus]?.color}
                    onClick={() => SelecionaUsuarioAceite(row.original.id)}
                  >
                    {usuariosStatus?.[rowStatus]?.titulo}
                  </Badge>
                </Tooltip>
              ) : (
                <Badge
                  p="2"
                  rounded="xl"
                  maxW="120px"
                  w="100%"
                  textAlign="center"
                  border="1px"
                  colorScheme={usuariosStatus?.[rowStatus]?.color}
                >
                  {usuariosStatus?.[rowStatus]?.titulo}
                </Badge>
              )}
            </>
          );
        },
      },
      {
        Header: 'Custo',
        accessor: 'custo_certificado',
      },
      {
        Header: 'Comissão sugerida',
        accessor: 'comissao_certificado',
      },
      {
        Header: 'último login',
        accessor: 'ultimo_login',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('#3f3f46', 'white');
          return (
            <>
              <Text fontWeight="medium" color={color}>
                {format(parseISO(row.original.ultimo_login), 'dd/MM/yyyy')}
              </Text>
              <Text fontWeight="medium" color={color} mt="1">
                {format(parseISO(row.original.ultimo_login), 'HH:mm')}
              </Text>
            </>
          );
        },
      },
      {
        Header: 'ações',
        accessor: 'acoes',
        Cell: ({ row }: { row: any }) => {
          return (
            <>
              <IconButton
                _focus={{ outline: 'none', boxShadow: 'none' }}
                aria-label="usuário"
                icon={<Info size={18} />}
                colorScheme="blue"
                variant="ghost"
                onClick={() => history.push(`/usuarios/${row.original.id}`)}
              />
            </>
          );
        },
      },
    ],
    [],
  );

  return (
    <DefaultPage>
      {showAceite && (
        <AceitaUsuarioModal
          open={showAceite}
          close={() => setShowAceite(false)}
          id_usuario={usuarioAceite}
        />
      )}

      <Flex mt="12" mx="4" justify="space-between">
        <Box>
          <Heading fontSize="2xl">Usuários Cadastrados</Heading>
        </Box>
        <Popover placement="bottom-start">
          {permite(PERMISSAO_CONVIDAR_PARCEIROS) && (
            <PopoverTrigger>
              <Button>Convidar Parceiros</Button>
            </PopoverTrigger>
          )}
          <PopoverContent>
            <PopoverArrow />
            <PopoverCloseButton color={color} />
            <PopoverHeader color={color}>Convidar novo parceiro</PopoverHeader>
            <PopoverBody>
              <Text color={color}>
                Peça para um novo parceiro se cadastrar por seu link, em seguida
                você pode confirmar o cadastro por essa tela
              </Text>
              <Flex justify="center" mt="2">
                <Button
                  w="100%"
                  onClick={() => {
                    toast({
                      title: 'Copiado',
                      status: 'success',
                      position: 'bottom-right',
                      duration: 600,
                      isClosable: true,
                    });
                    navigator.clipboard.writeText(`
                      ${window.location.origin}/create-account/${user?.id}
                    `);
                  }}
                >
                  <Copy />
                  <Text ml="1" fontWeight="bold" color={bg}>
                    seu link
                  </Text>
                </Button>
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>
      <SimpleGrid
        templateColumns={['1fr', '1fr', '1fr 1fr 1fr 1fr']}
        spacingX="15px"
        spacingY="15px"
        mt="4"
      >
        <Card
          titulo=""
          icon={<Users color="#3b82f6" />}
          valor={
            (cardsData?.[1] ?? 0) +
            (cardsData?.[2] ?? 0) +
            (cardsData?.[3] ?? 0)
          }
          descricao="Total de Parceiros"
          isLoading={isLoading}
        />
        <Card
          titulo=""
          icon={<UserCheck color="#22c55e" />}
          valor={cardsData?.[1] ?? 0}
          descricao="Parceiros Ativos"
          isLoading={isLoading}
        />
        <Card
          titulo=""
          icon={<UserX color="#ef4444" />}
          valor={cardsData?.[2] ?? 0}
          descricao="Parceiros Inativos"
          isLoading={isLoading}
        />
        <Card
          titulo=""
          icon={<UserCog color="#eab308" />}
          valor={cardsData?.[3] ?? 0}
          descricao="Aguardando confirmação"
          isLoading={isLoading}
        />
      </SimpleGrid>
      <SimpleGrid mt="1" templateColumns={['1fr', '1fr', '3fr 1fr']}>
        <Flex align="flex-end">
          <Input
            // ml="16px"
            mr="2px"
            name="pesquisa"
            leftIcon={Search}
            bg={bg}
            color={color}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            placeholder="Buscar por nome ou email"
            h="50"
            w="100%"
            fontWeight="lg"
          />
        </Flex>
        <Flex flexDir="column" ml="13px">
          <Text fontWeight="medium" fontSize="md" color={color}>
            Status
          </Text>
          <InputSelector
            rounded="2xl"
            h="50"
            color={color}
            bg={bg}
            value={filtroStatus}
            onChange={e => setFiltroStatus(e.target.value)}
            disableSelecione
            options={[
              { value: '0', label: 'Todos' },
              { value: '3', label: 'Aguardando confirmação' },
              { value: '1', label: 'Ativo' },
              { value: '2', label: 'Inativo' },
            ]}
          />
        </Flex>
      </SimpleGrid>
      <Box bg={bg} my="4" borderWidth="1px" rounded="2xl">
        <Table
          data={usuariosData}
          columns={colunas}
          isLoading={isFetching}
          pageIndex={page}
          pageSizeControl={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          tableLength={data?.length ?? 0}
          //
        />
      </Box>
    </DefaultPage>
  );
};

export default Usuarios;
